import { useCallback, useMemo } from 'react'
import { CreditApplicationPerson, CreditDocument } from '_gql'

import { useIntl } from '@divvy-web/i18n'

import { useGetCreditApplicationDetails } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'

const formatPersonName = (person: Pick<CreditApplicationPerson, 'firstName' | 'lastName'> | null) =>
  person ? `${person.firstName} ${person.lastName}` : ''

export const useDocumentTypes = ({ salesforceCreditId }: { salesforceCreditId: string }) => {
  const { formatMessage } = useIntl()

  const expiredLicenseMessage = formatMessage({
    defaultMessage: 'License cannot be expired.',
    id: 'sputnik.useDocumentTypes__fmqvsm',
  })

  const businessTaxReturnsMessage = formatMessage({
    defaultMessage: 'Business tax returns or tax transcripts from the last two years',
    id: 'sputnik.useDocumentTypes__6IBeH0',
  })

  const companyPreparedStatementsMessage = formatMessage({
    defaultMessage: 'Company prepared financial statements for the last two full years',
    id: 'sputnik.useDocumentTypes__PJIGcU',
  })

  const cpaReviewedStatementsMessage = formatMessage({
    defaultMessage: 'CPA reviewed or prepared statements for the last two full years',
    id: 'sputnik.useDocumentTypes__/OOTFE',
  })

  const cpaAuditedStatementsMessage = formatMessage({
    defaultMessage: 'Audited financial statements for the last two full years',
    id: 'sputnik.useDocumentTypes__h7He+2',
  })

  const interimFinancialsHeader = formatMessage({
    defaultMessage: 'Please provide the following financial document:',
    id: 'sputnik.useDocumentTypes__s4yQF+',
  })

  const interimFinancialsFooter = formatMessage({
    defaultMessage: 'Your company name must be clearly visible in all documents.',
    id: 'sputnik.useDocumentTypes__kECKWy',
  })

  const generatePersonValues = useCallback(
    ({ name, prefix }: { name: string; prefix: string }) => [
      {
        value: CreditDocument[`${prefix}_DRIVERS_LICENSE`],
        label: formatMessage(
          {
            defaultMessage: 'Drivers license front for {name}',
            id: 'sputnik.useDocumentTypes__cPOaFl',
          },
          {
            name,
          },
        ),
        header: formatMessage(
          {
            defaultMessage: 'Please provide a copy of {name}’s valid U.S. driver’s license. (front)',
            id: 'sputnik.useDocumentTypes__uQFUa7',
          },
          {
            name,
          },
        ),
        footer: expiredLicenseMessage,
      },
      {
        value: CreditDocument[`${prefix}_DRIVERS_LICENSE_BACK`],
        label: formatMessage(
          {
            defaultMessage: 'Drivers license back for {name}',
            id: 'sputnik.requiredDocumentMapper__UkxL/V',
          },
          {
            name,
          },
        ),
        header: formatMessage(
          {
            defaultMessage: 'Please provide a copy of {name}’s valid U.S. driver’s license. (back)',
            id: 'sputnik.useDocumentTypes__tYfeyU',
          },
          {
            name,
          },
        ),
        footer: expiredLicenseMessage,
      },
      {
        value: CreditDocument[`${prefix}_GREEN_CARD`],
        label: formatMessage(
          {
            defaultMessage: 'Green Card for {name}',
            id: 'sputnik.useDocumentTypes__dX1uQy',
          },
          {
            name,
          },
        ),
        header: formatMessage(
          {
            defaultMessage: 'Please provide a copy of {name}’s green card.',
            id: 'sputnik.useDocumentTypes__s00mU6',
          },
          { name },
        ),
      },
      {
        value: CreditDocument[`${prefix}_PASSPORT`],
        label: formatMessage(
          {
            defaultMessage: 'Passport for {name}',
            id: 'sputnik.useDocumentTypes__azfrRz',
          },
          {
            name,
          },
        ),
        header: formatMessage(
          {
            defaultMessage: 'Please provide a copy of {name}’s valid passport.',
            id: 'sputnik.useDocumentTypes__KHOJrP',
          },
          { name },
        ),
        footer: formatMessage({
          defaultMessage: 'Passport cannot be expired.',
          id: 'sputnik.useDocumentTypes__Ys5tTk',
        }),
      },
      {
        value: CreditDocument[`${prefix}_PHYSICAL_ADDRESS_VERIFICATION`],
        label: formatMessage(
          {
            defaultMessage: 'Residential address verification for {name}',
            id: 'sputnik.useDocumentTypes__JEjfE1',
          },
          {
            name,
          },
        ),
        header: formatMessage(
          {
            defaultMessage:
              'Please provide a third-party bill, agreement, or account statement that displays {name}’s name and physical address. Please provide one of the following',
            id: 'sputnik.useDocumentTypes__Su+gkh',
          },
          {
            name,
          },
        ),
        content: [
          formatMessage({
            defaultMessage: 'A lease agreement',
            id: 'sputnik.useDocumentTypes__tlgR4+',
          }),
          formatMessage({
            defaultMessage: 'Utility/phone bill',
            id: 'sputnik.useDocumentTypes__hyLhB9',
          }),
          formatMessage({
            defaultMessage: 'Insurance plan',
            id: 'sputnik.useDocumentTypes__SB/5N2',
          }),
          formatMessage({
            defaultMessage: 'Bank statement',
            id: 'sputnik.useDocumentTypes__Ga2LEW',
          }),
        ],
      },
      {
        value: CreditDocument[`${prefix}_SSN_CARD`],
        label: formatMessage(
          {
            defaultMessage: 'SSN card for {name}',
            id: 'sputnik.useDocumentTypes__FulIEf',
          },
          {
            name,
          },
        ),
        header: formatMessage(
          {
            defaultMessage: 'Please provide a copy of {name}’s SSN card.',
            id: 'sputnik.useDocumentTypes__lmImJP',
          },
          {
            name,
          },
        ),
      },
      {
        value: CreditDocument[`${prefix}_US_VISA`],
        label: formatMessage(
          {
            defaultMessage: 'US visa for {name}',
            id: 'sputnik.useDocumentTypes__rQpiXT',
          },
          {
            name,
          },
        ),
        header: formatMessage(
          {
            defaultMessage: 'Please provide a copy of {name}’s valid U.S. visa.',
            id: 'sputnik.useDocumentTypes__ij79wO',
          },
          { name },
        ),
        footer: formatMessage({
          defaultMessage: 'Visa cannot be expired.',
          id: 'sputnik.useDocumentTypes__C5bTBy',
        }),
      },
    ],
    [expiredLicenseMessage, formatMessage],
  )

  const generateAuthorizedSignerValues = useCallback(
    ({ name }: { name: string }) => generatePersonValues({ name, prefix: 'CP' }),
    [generatePersonValues],
  )

  const generateBeneficialOwnerValues = useCallback(
    ({ name, index }: { name: string; index: number }) => generatePersonValues({ name, prefix: `BO${index}` }),
    [generatePersonValues],
  )

  const getAllDocumentTypes = useCallback(
    ({
      authorizedSignerName,
      beneficialOwner1Name,
      beneficialOwner2Name,
      beneficialOwner3Name,
      beneficialOwner4Name,
      accountNumberLastFour,
      desiredCredit,
    }: {
      authorizedSignerName: string
      beneficialOwner1Name?: string
      beneficialOwner2Name?: string
      beneficialOwner3Name?: string
      beneficialOwner4Name?: string
      accountNumberLastFour?: string
      desiredCredit?: number
    }) => {
      const desiredCreditInThousands = (desiredCredit ?? 0) / 100000

      return [
        {
          value: CreditDocument.BANK_STATEMENT,
          label: formatMessage({
            defaultMessage: 'Bank statement',
            id: 'sputnik.useDocumentTypes__Ga2LEW',
          }),
          header: formatMessage(
            {
              defaultMessage:
                'Please provide a bank statement for account ending in ••••{accountNumberLastFour}. Must be dated within the last 90 days and include transaction history.',
              id: 'sputnik.useDocumentTypes__cYuIet',
            },
            {
              accountNumberLastFour,
            },
          ),
        },
        {
          value: CreditDocument.BANK_VERIFICATION,
          label: formatMessage({
            defaultMessage: 'Bank verification',
            id: 'sputnik.useDocumentTypes__Mw2CGN',
          }),
          content: [
            formatMessage({
              defaultMessage: 'Include the physical address of your business',
              id: 'sputnik.useDocumentTypes__UVN0D5',
            }),
            formatMessage({
              defaultMessage: 'Include the full routing and account number provided in your application',
              id: 'sputnik.useDocumentTypes__ZKXdI7',
            }),
            formatMessage({
              defaultMessage: 'Be dated within the last 90 days',
              id: 'sputnik.useDocumentTypes__aiEIAr',
            }),
          ],
          header: formatMessage(
            {
              defaultMessage:
                'Please provide a voided check or account statement for the business account ending in ••••{accountNumberLastFour}. This must include the following:',
              id: 'sputnik.useDocumentTypes__0J1hOk',
            },
            {
              accountNumberLastFour,
            },
          ),
        },
        {
          value: CreditDocument.BPAV,
          label: formatMessage({
            defaultMessage: 'Business physical address',
            id: 'sputnik.useDocumentTypes__4IvIJZ',
          }),
          content: [
            formatMessage({
              defaultMessage: 'A utility bill',
              id: 'sputnik.useDocumentTypes__FhL5CL',
            }),
            formatMessage({
              defaultMessage: 'Insurance plan',
              id: 'sputnik.useDocumentTypes__SB/5N2',
            }),
            formatMessage({
              defaultMessage: 'Bank statement dated within 90 days',
              id: 'sputnik.useDocumentTypes__O3K86k',
            }),
            formatMessage({
              defaultMessage: 'A fully executed lease agreement',
              id: 'sputnik.useDocumentTypes__U1ukKa',
            }),
          ],
          header: formatMessage({
            defaultMessage:
              'Please provide a third party bill, agreement, or account statement that displays both the business name and the address. Please provide one of the following:',
            id: 'sputnik.useDocumentTypes__Wb4gIM',
          }),
        },
        {
          value: CreditDocument.BUSINESS_REGISTRATION,
          label: formatMessage({
            defaultMessage: 'Business registration',
            id: 'sputnik.useDocumentTypes__DfaxEL',
          }),
          content: [
            formatMessage({
              defaultMessage: 'A copy of your Certificate of Good Standing',
              id: 'sputnik.useDocumentTypes__Rgr4jJ',
            }),
            formatMessage({
              defaultMessage: 'An unexpired business license',
              id: 'sputnik.useDocumentTypes__5MdYpn',
            }),
            formatMessage({
              defaultMessage: 'Articles of Incorporation/Organization',
              id: 'sputnik.useDocumentTypes__tsZsxm',
            }),
            formatMessage({
              defaultMessage: 'Business Registration Partnership Agreement',
              id: 'sputnik.useDocumentTypes__cDWFBi',
            }),
            formatMessage({
              defaultMessage: 'Trust Agreement',
              id: 'sputnik.useDocumentTypes__Simj3t',
            }),
          ],
          header: formatMessage({
            defaultMessage: 'Please provide one of the following:',
            id: 'sputnik.useDocumentTypes__pwiZ9z',
          }),
          footer: formatMessage({
            defaultMessage: 'Documents must be dated within the last year and cannot be expired.',
            id: 'sputnik.useDocumentTypes__xXyMWB',
          }),
        },
        {
          value: CreditDocument.BUSINESS_STATUS,
          label: formatMessage({
            defaultMessage: 'Business status',
            id: 'sputnik.useDocumentTypes__oK381n',
          }),
          content: [
            formatMessage({
              defaultMessage: 'A copy of your Certificate of Good Standing',
              id: 'sputnik.useDocumentTypes__Rgr4jJ',
            }),
            formatMessage({
              defaultMessage: 'Partnership Agreement (for trusts, partnerships or sole proprietorships)',
              id: 'sputnik.useDocumentTypes__RBUUxG',
            }),
            formatMessage({
              defaultMessage: 'Trust Agreement (for trusts, partnerships or sole proprietorships)',
              id: 'sputnik.useDocumentTypes__p0QKzg',
            }),
          ],
          footer: formatMessage({
            defaultMessage: 'Documents must be dated within the last year and cannot be expired.',
            id: 'sputnik.useDocumentTypes__xXyMWB',
          }),
          header: formatMessage({
            defaultMessage: 'Please provide one of the following:',
            id: 'sputnik.useDocumentTypes__pwiZ9z',
          }),
        },
        {
          value: CreditDocument.BUSINESS_TAX_RETURN,
          label: formatMessage({
            defaultMessage: 'Business tax return',
            id: 'sputnik.useDocumentTypes__li91F0',
          }),
          header: businessTaxReturnsMessage,
          footer: formatMessage({
            defaultMessage:
              'This document cannot be submitted as a screenshot. The company name must be clearly visible',
            id: 'sputnik.useDocumentTypes__Hbq8Up',
          }),
        },
        {
          value: CreditDocument.COMPANY_PREPARED,
          label: formatMessage({
            defaultMessage: 'Company prepared financial statements',
            id: 'sputnik.useDocumentTypes__gDyM1w',
          }),
          header: companyPreparedStatementsMessage,
        },
        {
          value: CreditDocument.CPA_AUDITED,
          label: formatMessage({
            defaultMessage: 'CPA audited financial statements',
            id: 'sputnik.useDocumentTypes__bV44g9',
          }),
          header: cpaAuditedStatementsMessage,
        },
        {
          value: CreditDocument.CPA_COMPILED,
          label: formatMessage({
            defaultMessage: 'CPA compiled financial statements',
            id: 'sputnik.useDocumentTypes__o/8Cy5',
          }),
          header: formatMessage({
            defaultMessage: 'CPA prepared financial statements for the last two full years',
            id: 'sputnik.useDocumentTypes__mQxhO3',
          }),
        },
        {
          value: CreditDocument.CPA_REVIEW,
          label: formatMessage({
            defaultMessage: 'CPA reviewed financial statements',
            id: 'sputnik.useDocumentTypes__Mgo0U1',
          }),
          header: formatMessage({
            defaultMessage: 'CPA reviewed financial statements for the last two full years',
            id: 'sputnik.useDocumentTypes__0IIFuL',
          }),
        },
        {
          value: CreditDocument.CRB_PG_AGREEMENT,
          label: formatMessage({
            defaultMessage: 'CRB PG agreement',
            id: 'sputnik.useDocumentTypes__22wKNJ',
          }),
          header: formatMessage({
            defaultMessage: 'Signed CRB Personal Guarantee Agreement',
            id: 'sputnik.useDocumentTypes__88bd3k',
          }),
        },
        {
          value: CreditDocument.FINANCIAL_DOCUMENTS,
          label: formatMessage({
            defaultMessage: 'Financial documents',
            id: 'sputnik.useDocumentTypes__Wte4+P',
          }),
          content:
            desiredCreditInThousands <= 250
              ? [companyPreparedStatementsMessage, businessTaxReturnsMessage]
              : desiredCreditInThousands <= 750
              ? [businessTaxReturnsMessage, cpaReviewedStatementsMessage]
              : desiredCreditInThousands <= 1500
              ? [cpaReviewedStatementsMessage]
              : [cpaAuditedStatementsMessage],
          header: formatMessage({
            defaultMessage: 'Please provide one of the following financial documents:',
            id: 'sputnik.useDocumentTypes__tOHIpu',
          }),
          footer: formatMessage({
            defaultMessage: 'Your company name must be clearly visible in all documents.',
            id: 'sputnik.useDocumentTypes__kECKWy',
          }),
        },
        {
          value: CreditDocument.INTERIM_FINANCIALS,
          label: formatMessage({
            defaultMessage: 'Interim financials',
            id: 'sputnik.useDocumentTypes__zaa3fA',
          }),
          content: [
            formatMessage({
              defaultMessage: 'Interim financials for the current year',
              id: 'sputnik.useDocumentTypes__nuJ9/k',
            }),
          ],
          header: interimFinancialsHeader,
          footer: interimFinancialsFooter,
        },
        {
          value: CreditDocument.NDA,
          label: formatMessage({
            defaultMessage: 'Non-Disclosure agreement',
            id: 'sputnik.useDocumentTypes__e+Wr7f',
          }),
          header: formatMessage({
            defaultMessage: 'A non-disclosure agreement that is associated with your application',
            id: 'sputnik.useDocumentTypes__WzVcXn',
          }),
        },
        {
          value: CreditDocument.PERSONAL_TAX_RETURN,
          label: formatMessage({
            defaultMessage: 'Personal tax return',
            id: 'sputnik.useDocumentTypes__bxMyms',
          }),
          header: formatMessage({
            defaultMessage: 'Personal tax returns or tax transcripts from the last two years',
            id: 'sputnik.useDocumentTypes__AFVGX9',
          }),
          footer: formatMessage({
            defaultMessage:
              'This document cannot be submitted as a screenshot. Your personal name must be clearly visible.',
            id: 'sputnik.useDocumentTypes__2vayI5',
          }),
        },
        {
          value: CreditDocument.PUBLIC_INTERIM_FINANCIALS,
          label: formatMessage({
            defaultMessage: 'Public interim financials',
            id: 'sputnik.useDocumentTypes__GgqHMb',
          }),
          content: [
            formatMessage({
              defaultMessage: 'Public interim financials for the current year',
              id: 'sputnik.useDocumentTypes__P/e1Lj',
            }),
          ],
          header: interimFinancialsHeader,
          footer: interimFinancialsFooter,
        },
        {
          value: CreditDocument.TAX_ID,
          label: formatMessage({
            defaultMessage: 'Tax ID',
            id: 'sputnik.useDocumentTypes__Yl8GBP',
          }),
          content: [
            formatMessage({
              defaultMessage: 'A letter from the IRS',
              id: 'sputnik.useDocumentTypes__qOBwcD',
            }),
            formatMessage({
              defaultMessage: 'Your tax return',
              id: 'sputnik.useDocumentTypes__SzvKL8',
            }),
            formatMessage({
              defaultMessage: 'A W-9 form',
              id: 'sputnik.useDocumentTypes__9qwYso',
            }),
          ],
          footer: formatMessage({
            defaultMessage: 'Documents must be dated with the last year and cannot be expired.',
            id: 'sputnik.useDocumentTypes__afp+pf',
          }),
          header: formatMessage({
            defaultMessage:
              "Please provide documentation that clearly displays your business's legal name, tax ID number, and preferably also includes your business physical address. You may provide one of the following:",
            id: 'sputnik.useDocumentTypes__Geq5Iq',
          }),
        },
        {
          value: CreditDocument.OTHER,
          label: formatMessage({
            defaultMessage: 'Other',
            id: 'sputnik.useDocumentTypes__/VnDMl',
          }),
          header: formatMessage({
            defaultMessage:
              'Any document you would like our team to review that is not encapsulated by the options provided',
            id: 'sputnik.useDocumentTypes__abBXKO',
          }),
        },
        ...(authorizedSignerName ? generateAuthorizedSignerValues({ name: authorizedSignerName }) : []),
        ...(beneficialOwner1Name
          ? generateBeneficialOwnerValues({
              name: beneficialOwner1Name,
              index: 1,
            })
          : []),
        ...(beneficialOwner2Name
          ? generateBeneficialOwnerValues({
              name: beneficialOwner2Name,
              index: 2,
            })
          : []),
        ...(beneficialOwner3Name
          ? generateBeneficialOwnerValues({
              name: beneficialOwner3Name,
              index: 3,
            })
          : []),
        ...(beneficialOwner4Name
          ? generateBeneficialOwnerValues({
              name: beneficialOwner4Name,
              index: 4,
            })
          : []),
      ]
    },
    [
      businessTaxReturnsMessage,
      companyPreparedStatementsMessage,
      cpaAuditedStatementsMessage,
      cpaReviewedStatementsMessage,
      formatMessage,
      generateAuthorizedSignerValues,
      generateBeneficialOwnerValues,
      interimFinancialsFooter,
      interimFinancialsHeader,
    ],
  )

  const { data, loading } = useGetCreditApplicationDetails({
    variables: {
      creditApplicationId: salesforceCreditId,
    },
  })

  const options = useMemo(() => {
    if (!data || loading) {
      return []
    }

    const { authorizedSigner, beneficialOwners, financeInfo } = data.creditApplication ?? {
      authorizedSigner: null,
      beneficialOwners: [],
    }

    const { desiredCredit, selectedBankInfo } = financeInfo ?? {}
    const { accountNumberLastFour } = selectedBankInfo ?? {}

    const presentBeneficialOwners =
      beneficialOwners
        ?.filter((owner) => owner !== null)
        .reduce(
          (acc, curr, index) => ({
            ...acc,
            [`beneficialOwner${index + 1}Name`]: formatPersonName(curr),
          }),
          {},
        ) ?? {}

    return getAllDocumentTypes({
      authorizedSignerName: formatPersonName(authorizedSigner),
      ...presentBeneficialOwners,
      accountNumberLastFour: accountNumberLastFour ?? '',
      desiredCredit: desiredCredit ?? 0,
    })
  }, [data, getAllDocumentTypes, loading])

  return { options, loading }
}
